import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

export type ValueOption = {
  value: string,
  readableValue: string,
  icon?: string | JSX.Element
};

type Props = {
  values?: ValueOption[],
  label?: string,
  onChange: (value: string) => void,
  isDisabled?: boolean,
  defaultValue?: string,
  helperText?: string
};

function SelectInputV2({ values = [], label = '', isDisabled = false, onChange = () => null, defaultValue = '', helperText }: Props) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectRef]);

  const handleSelect = (value: string) => {
    setSelectedValue(value);
    onChange(value);
    setIsOpen(false);
  };

  const selectedOption = values.find(v => v.value === selectedValue);

  return (
    <div className="flex-1 max-w-full" ref={selectRef}>
      {label && <p className="text-sm font-extrabold">{t(label)}:</p>}
      <div
        data-dd-action-name={'Dropdown Select'}
        className={`relative w-full border-stone-200 border rounded focus:outline-none flex justify-between items-center ${isDisabled ? 'bg-stone-100 cursor-not-allowed' : 'bg-white cursor-pointer'}`}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
      >
        <div className="px-1 pt-3 pb-3 flex items-center">
          {selectedOption?.icon && typeof selectedOption.icon === 'string' ? (
            <img src={selectedOption.icon} alt="" className="inline mr-2 w-4 h-auto" />
          ) : (
            selectedOption?.icon && <span className="inline mr-2 w-4 h-auto">{selectedOption.icon}</span>
          )}
          <span>{t(selectedOption?.readableValue || '')}</span>
        </div>
        <div className="mr-2">
          <svg className={`w-4 h-4 transform ${isOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        {isOpen && (
          <div className="absolute z-10 w-full bg-white border border-stone-200 rounded shadow-md mt-1">
          {values.map((m, i) => (
            <div
              data-dd-action-name={'Dropdown Select Option'}
              key={i}
              onClick={() => handleSelect(m.value)}
              className="cursor-pointer hover:bg-stone-100 px-3 py-2 flex items-center justify-between"
            >
              <div className="flex items-center">
                {m.icon && typeof m.icon === 'string' ? (
                  <img src={m.icon} alt="" className="inline mr-2 w-4 h-auto" />
                ) : (
                  m.icon && <span className="inline mr-2 w-4 h-auto">{m.icon}</span>
                )}
                <span>{t(m.readableValue)}</span>
              </div>
              {m.value === selectedValue && (
                <span className="text-green-300 ml-2">
                  <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              )}
            </div>
          ))}
        </div>
        )}
      </div>
      {helperText && <p className="text-xs text-gray-500 mt-1">{helperText}</p>}
    </div>
  );
}

export default SelectInputV2;